import { useLayoutEffect, useRef } from 'react'
import type { ReactNode, Ref, SyntheticEvent } from 'react'
import { Resizable } from 'react-resizable'
import type { ResizeHandleAxis } from 'react-resizable'
import * as S from './ResizeControls.styled'
import { useRecoilValue } from 'recoil'
import { pageZoomAtom } from 'atoms'
import { editorItemLockedSelector } from '@/atoms/editorTextBoxAtoms'
import { ResizeHandleElement } from 'components/page/Items/ResizeHandle'
import { roundItemSizePx, useItemResize } from '@/hooks/editor/useItemResize'
import { anchoredPosition } from '@/utils/editor/resizeAnchor'
import type { ResizeGestureStart } from '@/utils/editor/resizeAnchor'
import { MIN_ITEM_RESIZE_PX } from '@/utils/editor/itemTypes'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import {
  EDITOR_TEXT_SCALING_ENABLED,
  isEditorCornerHandle,
  isEditorTextBoxItemType,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

type Controlled<T> = [T, (value: T) => void]

type ResizeCallbackData = {
  size: { width: number; height: number }
  handle: string
}

type GestureStart = ResizeGestureStart & {
  handle: string
  pointer: { x: number; y: number } | null
}

const pointerOf = (event: SyntheticEvent): { x: number; y: number } | null => {
  const e = event as unknown as { clientX?: unknown; clientY?: unknown }
  return typeof e.clientX === 'number' && typeof e.clientY === 'number'
    ? { x: e.clientX, y: e.clientY }
    : null
}

type ResizeControlsProps = {
  x?: number
  y?: number
  z?: number
  width?: number | string
  height?: number | string
  rotate?: number
  internalWidth?: Controlled<number>
  internalHeight?: Controlled<number>
  controlledX?: Controlled<number>
  controlledY?: Controlled<number>
  commitPosition?: (x: number, y: number) => void
  type?: string
  selected?: boolean
  disabled?: boolean
  resizingState?: Controlled<string>
  onResizeHandleChange?: (handle: string | null) => void
  onEditorTextBoxCornerScale?: (scale: number) => void
  /**
   * The height an editor text box's text needs at the sizes it is painted
   * at, read at every pointer move: the floor a held handle's height is
   * clamped to while text scaling is off.
   */
  textNeedPx?: () => number | null
  children?: ReactNode
  id?: string
}

const EDITOR_TEXT_BOX_HANDLES: ResizeHandleAxis[] = [
  'se',
  'sw',
  'ne',
  'nw',
  'e',
  'w',
  'n',
  's',
]

/**
 * A corner scale is aspect locked: the axis the pointer moved farther sets one
 * factor k, and the frame's width and height both follow it. The factor is
 * read from the pointer's own travel in page px, never from the size the
 * library hands back, which its item minimum has already clamped — a click on
 * the corner of a box shorter than that minimum would otherwise scale the box.
 */
export const cornerScaleFactor = (
  handle: string,
  start: { width: number; height: number },
  travel: { dx: number; dy: number },
): number => {
  if (!(start.width > 0) || !(start.height > 0)) return 1
  const dw = handle.includes('w') ? -travel.dx : travel.dx
  const dh = handle.includes('n') ? -travel.dy : travel.dy
  const byWidth = dw / start.width
  const byHeight = dh / start.height
  const k = 1 + (Math.abs(byWidth) >= Math.abs(byHeight) ? byWidth : byHeight)
  const floor = Math.max(
    MIN_ITEM_RESIZE_PX / start.width,
    MIN_ITEM_RESIZE_PX / start.height,
  )
  return Math.max(floor, k)
}

/** The whole pixel a held handle's height is clamped to: the text's need, or nothing. */
const textNeedFloorPx = (textNeedPx: number | null): number =>
  textNeedPx !== null && Number.isFinite(textNeedPx) && textNeedPx > 0
    ? Math.ceil(textNeedPx - 1e-6)
    : 0

/**
 * The frame a held handle asks for on an editor text box while text scaling
 * is off (`EDITOR_TEXT_SCALING_ENABLED`): the pointer's frame, held at or
 * above the height the text needs at its painted sizes. A corner is two
 * pills at once — the width and the height each follow the pointer, nothing
 * couples them — and its height stops at the text, rising while a narrowing
 * width wraps the lines taller and coming back down to the pointer's own
 * height once the wrap fits again. A side pill's height is the height the
 * drag started at, or the wrap's need above it, so widening back brings the
 * frame down to the start and never lower. A top or bottom pill stops at the
 * text. The need is the read-only fit's or the editing host's last
 * measurement, a frame behind the pointer at most; the fit that follows each
 * move raises the frame itself when the measurement moves.
 */
export const heldTextBoxFrame = (
  handle: string,
  start: { width: number; height: number },
  asked: { width: number; height: number },
  textNeedPx: number | null,
): { width: number; height: number } => {
  const floor = textNeedFloorPx(textNeedPx)
  if (isEditorCornerHandle(handle))
    return { width: asked.width, height: Math.max(asked.height, floor) }
  const widthPill = handle.includes('e') || handle.includes('w')
  if (widthPill)
    return { width: asked.width, height: Math.max(start.height, floor) }
  return { width: asked.width, height: Math.max(asked.height, floor) }
}

/**
 * The resize frame of an editor text box or rule. A classic (TinyMCE-era)
 * item resizes through `ClassicResizeControls.tsx`, the staging frame.
 */
export const ResizeControls = ({
  rotate,
  internalWidth: [internalWidth, setInternalWidth] = [0, () => {}],
  internalHeight: [internalHeight, setInternalHeight] = [0, () => {}],
  controlledX: [controlledX, setControlledX] = [0, () => {}],
  controlledY: [controlledY, setControlledY] = [0, () => {}],
  commitPosition = () => {},
  type,
  selected,
  disabled,
  resizingState: [, setResizingState] = ['idle', () => {}],
  onResizeHandleChange,
  onEditorTextBoxCornerScale,
  textNeedPx,
  children,
  id,
}: ResizeControlsProps) => {
  // The box's own session lock (a rule follows the page's lock setting): the
  // functional handles hide under it.
  const itemLocked = useRecoilValue(editorItemLockedSelector(id ?? ''))
  const zoom = useRecoilValue(pageZoomAtom)

  const { setSize } = useItemResize(id || '')

  const gestureStart = useRef<GestureStart | null>(null)
  const lastSize = useRef<{ width: number; height: number } | null>(null)

  // The live size can move while a handle is held without a pointer move
  // of its own: the settle raises an editor text box's frame to the lines a
  // narrowing corner wrapped taller. The edge the handle anchors to (the
  // bottom under a north handle, the right under a west one) follows the
  // size here, from the same start the pointer's own moves anchor to; a
  // move the pointer made already left the position at this value, so the
  // effect sets nothing then. (The raise the release's own fit can add
  // afterwards is anchored by the page, which owns that fit.)
  useLayoutEffect(() => {
    const start = gestureStart.current
    if (start === null) return
    const pos = anchoredPosition(start.handle, start, {
      width: internalWidth,
      height: internalHeight,
    })
    if (Number.isFinite(pos.x) && pos.x !== controlledX) setControlledX(pos.x)
    if (Number.isFinite(pos.y) && pos.y !== controlledY) setControlledY(pos.y)
  }, [
    internalWidth,
    internalHeight,
    controlledX,
    controlledY,
    setControlledX,
    setControlledY,
  ])

  const isEditorTextBox = isEditorTextBoxItemType(type)

  const handlePlacements: ResizeHandleAxis[] = EDITOR_TEXT_BOX_HANDLES

  // With text scaling on only the item minimum binds a handle: a text box
  // height pill reaches any height in every state and the release scales
  // the text into it. With it off the text's need is the library's minimum
  // height as well as `constrainedSize`'s floor, so the library's own slack
  // applies: a pill held under the text waits for the pointer to come back
  // before the frame follows it up again.
  const textFloorPx =
    isEditorTextBox && !EDITOR_TEXT_SCALING_ENABLED
      ? textNeedFloorPx(textNeedPx?.() ?? null)
      : 0
  const minConstraints: [number, number] = [
    MIN_ITEM_RESIZE_PX,
    Math.max(MIN_ITEM_RESIZE_PX, textFloorPx),
  ]

  const draggedAxes = (
    handle: string,
  ): { width: boolean; height: boolean } => ({
    width: handle.includes('e') || handle.includes('w'),
    height: handle.includes('n') || handle.includes('s'),
  })

  /**
   * The frame a handle asks for. A pill moves its one axis wherever the
   * pointer takes it, and so does each axis of a corner — the corner is
   * aspect locked only while text scaling is on, where one factor has to
   * carry the frame and the authored sizes together. With text scaling off
   * the frame is then held above the text (`heldTextBoxFrame`).
   */
  const constrainedSize = (
    handle: string,
    start: GestureStart | null,
    size: { width: number; height: number },
    pointer: { x: number; y: number } | null,
  ): { width: number; height: number } => {
    const axes = draggedAxes(handle)
    let width = axes.width ? size.width : internalWidth
    let height = axes.height ? size.height : internalHeight
    if (isEditorTextBox && start) {
      if (EDITOR_TEXT_SCALING_ENABLED && isEditorCornerHandle(handle)) {
        // Without a pointer the travel is read from the size, measured from
        // the start size as the library's item minimum has already raised
        // it, so a box shorter than that minimum does not read as a scale.
        const travel =
          pointer && start.pointer
            ? {
                dx: (pointer.x - start.pointer.x) / (zoom * 0.01),
                dy: (pointer.y - start.pointer.y) / (zoom * 0.01),
              }
            : {
                dx:
                  (handle.includes('w') ? -1 : 1) *
                  (size.width - Math.max(start.width, MIN_ITEM_RESIZE_PX)),
                dy:
                  (handle.includes('n') ? -1 : 1) *
                  (size.height - Math.max(start.height, MIN_ITEM_RESIZE_PX)),
              }
        const k = cornerScaleFactor(handle, start, travel)
        width = Math.max(MIN_ITEM_RESIZE_PX, start.width * k)
        height = Math.max(MIN_ITEM_RESIZE_PX, start.height * k)
      }
      if (!EDITOR_TEXT_SCALING_ENABLED)
        return heldTextBoxFrame(
          handle,
          start,
          { width, height },
          textNeedPx?.() ?? null,
        )
    }
    return { width, height }
  }

  const onResizeStart = (
    event: SyntheticEvent,
    { handle }: ResizeCallbackData,
  ) => {
    gestureStart.current = {
      handle,
      x: controlledX,
      y: controlledY,
      width: internalWidth,
      height: internalHeight,
      pointer: pointerOf(event),
    }
    countEditorProbeRender(
      `ResizeControls.start:${String(id).slice(0, 8)}:${handle}:${internalWidth}x${internalHeight}`,
    )
    onResizeHandleChange?.(handle)
    setResizingState('start')
  }

  const onResize = (
    event: SyntheticEvent,
    { size, handle }: ResizeCallbackData,
  ) => {
    const start = gestureStart.current
    const h = handle ?? start?.handle ?? ''
    const next = constrainedSize(h, start, size, pointerOf(event))
    countEditorProbeRender(
      `ResizeControls.resize:${String(id).slice(0, 8)}:${h}:${next.width}x${next.height}`,
    )
    lastSize.current = next
    const axes = draggedAxes(h)
    if (axes.width || isEditorCornerHandle(h)) setInternalWidth(next.width)
    if (axes.height || isEditorCornerHandle(h)) setInternalHeight(next.height)
    if (start) {
      const pos = anchoredPosition(h, start, next)
      setControlledX(pos.x)
      setControlledY(pos.y)
    }
    setResizingState('resizing')
  }

  const onResizeStop = (
    event: SyntheticEvent,
    { size, handle }: ResizeCallbackData,
  ) => {
    const start = gestureStart.current
    gestureStart.current = null
    const h = handle ?? start?.handle ?? ''
    const pointer = pointerOf(event)
    // A release with no pointer of its own (a mouseup the library relays
    // without coordinates) keeps the frame the last move asked for.
    const next =
      pointer !== null || !isEditorCornerHandle(h)
        ? constrainedSize(h, start, size, pointer)
        : (lastSize.current ?? constrainedSize(h, start, size, null))
    lastSize.current = null
    const committedWidth = roundItemSizePx(next.width)
    const committedHeight = roundItemSizePx(next.height)
    countEditorProbeRender(
      `ResizeControls.stop:${String(id).slice(0, 8)}:${h}:${committedWidth}x${committedHeight}`,
    )
    // The live size is the committed one from this render on, so the fit
    // that follows the release measures the frame the release wrote, not
    // the frame the last pointer move left when the pointer moved once more
    // before it let go.
    setInternalWidth(committedWidth)
    setInternalHeight(committedHeight)
    setSize(committedWidth, committedHeight)
    if (
      isEditorTextBox &&
      isEditorCornerHandle(h) &&
      start &&
      start.width > 0 &&
      typeof onEditorTextBoxCornerScale === 'function'
    )
      onEditorTextBoxCornerScale(committedWidth / start.width)
    if (start) {
      const pos = anchoredPosition(h, start, {
        width: committedWidth,
        height: committedHeight,
      })
      const committedX = Math.round(pos.x)
      const committedY = Math.round(pos.y)
      if (
        Number.isFinite(committedX) &&
        Number.isFinite(committedY) &&
        (committedX !== start.x || committedY !== start.y)
      ) {
        setControlledX(committedX)
        setControlledY(committedY)
        commitPosition(committedX, committedY)
      }
    }
    setResizingState('stop')
    setTimeout(() => {
      setResizingState('idle')
      onResizeHandleChange?.(null)
    }, 0)
  }

  // The controlled `Resizable`: the frame's size is the item's live size the
  // page holds, and every pointer move goes through `constrainedSize` before
  // it lands there. (Its uncontrolled sibling `ResizableBox` keeps a copy of
  // the size it computes itself, which a clamp that leaves the page's size
  // unchanged never reaches.)
  const frameWidth = internalWidth || 0
  const frameHeight = internalHeight || 0
  return (
    <Resizable
      height={frameHeight}
      width={frameWidth}
      onResizeStart={onResizeStart}
      onResize={onResize}
      onResizeStop={onResizeStop}
      minConstraints={minConstraints}
      resizeHandles={handlePlacements}
      axis="both"
      transformScale={zoom * 0.01}
      handle={(position: ResizeHandleAxis, ref: Ref<HTMLDivElement>) => {
        return (
          <ResizeHandleElement
            innerRef={ref}
            itemLocked={itemLocked}
            position={position}
            selected={selected}
            itemId={id}
          />
        )
      }}
    >
      <S.ResizeControls
        style={{ width: `${frameWidth}px`, height: `${frameHeight}px` }}
        rotate={rotate}
        selected={selected}
        disabled={disabled}
      >
        {children}
      </S.ResizeControls>
    </Resizable>
  )
}

export default ResizeControls
